import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

class CartDrawerComponent extends Component {
  #summaryThreshold = 0.5;

  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.addEventListener('close', this.#handleDrawerClose);

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.removeEventListener('close', this.#handleDrawerClose);
  }

  #handleDrawerClose = () => {
    // 1. Force Page Wrapper to reset (Horizon "Squeeze" fix)
    const pageWrapper = document.querySelector('.page-wrapper');
    if (pageWrapper) {
      pageWrapper.classList.remove('page-wrapper--drawer-open');
      pageWrapper.style.transition = ''; // Reset transitions
    }
    
    // 2. Clear lingering dialog styles
    const dialog = this.#dialog;
    if (dialog) {
      dialog.style.removeProperty('padding');
      dialog.style.removeProperty('min-width');
    }
    
    // 3. Notify the theme that the drawer is closed
    document.dispatchEvent(new CustomEvent('theme-drawer:close'));
  };

  #handleDrawerOpen = () => {
    this.#updateStickyState();
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;
    const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise?.then(() => {
      // --- HEADER SYNC ---
      fetch('/cart.js')
        .then(res => res.json())
        .then(cartData => {
           const countElement = document.querySelector('.cart-bubble__text-count');
           const bubbleContainer = document.querySelector('.cart-bubble');
           const cartIconWrapper = document.querySelector('cart-icon'); 
           
           if (countElement) countElement.textContent = cartData.item_count;

           if (bubbleContainer && cartIconWrapper) {
              if (cartData.item_count === 0) {
                  bubbleContainer.classList.add('hidden', 'is-empty');
                  bubbleContainer.style.display = 'none';
                  cartIconWrapper.classList.remove('header-actions__cart-icon--has-cart');
              } else {
                  bubbleContainer.classList.remove('hidden', 'is-empty');
                  bubbleContainer.style.display = 'flex';
                  cartIconWrapper.classList.add('header-actions__cart-icon--has-cart');
              }
           }
        }).catch(err => console.error('Sync failed', err));
      // --------------------
      this.#updateStickyState();
    });
  };

  #isCartEmpty() { return Boolean(this.querySelector('.cart-drawer--empty')); }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');
    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }
    const ratio = summary.getBoundingClientRect().height / dialog.getBoundingClientRect().height;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}